import { useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Title,
  Card,
  Text,
  Stack,
  Group,
  Button,
  Avatar,
  Textarea,
  Divider,
  Checkbox,
  Select,
  NumberInput,
  Paper,
  Loader,
  Alert
} from '@mantine/core';
import {
  IconArrowLeft,
  IconHeart,
  IconUser,
  IconSparkles,
  IconInfoCircle,
  IconExclamationCircle
} from '@tabler/icons-react';
import { useApi, useApiClient } from '../api';
import { notifications } from '@mantine/notifications';
import { formatCents } from '../components/products/productDisplay';

const SpoilPrincessPage = () => {
  const { princessId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Data passed via navigation state (from PrincessDetailPage)
  const passedPrincess = location.state?.princess;
  const passedProduct = location.state?.pedicureProduct;

  // Fallback fetch if navigated directly
  const { data: fetchedPrincess, loading: princessLoading } = useApi(
    `/api/v1/sellers/${princessId}`,
    undefined,
    { enabled: !passedPrincess }
  );
  const { data: productsData, loading: productsLoading } = useApi(
    '/api/v1/products',
    { seller_id: princessId, limit: 100 },
    { enabled: !passedProduct }
  );

  const princess = passedPrincess || fetchedPrincess;

  const fetchedProducts = productsData?.items ?? [];
  const pedicureProduct = passedProduct || (
    fetchedProducts.find(p => /pedicure/i.test(p.title)) || fetchedProducts[0]
  );

  const loading =
    (!passedPrincess && princessLoading) || (!passedProduct && productsLoading);

  const api = useApiClient();

  const [nailDescription, setNailDescription] = useState('');
  const [serviceType, setServiceType] = useState('gel');
  const [numberOfPictures, setNumberOfPictures] = useState(5);
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);

  const princessName = princess?.business_name || 'your princess';
  const price = formatCents(pedicureProduct?.price_cents);

  const handleSubmit = async () => {
    if (!nailDescription.trim()) {
      notifications.show({ title: 'Required', message: 'Please describe the nail polish or design you want.', color: 'orange' });
      return;
    }
    if (!agreed) {
      notifications.show({ title: 'Agreement required', message: 'Please agree to the spoil agreement before continuing.', color: 'orange' });
      return;
    }

    setSubmitting(true);
    setSubmitError(null);
    try {
      // Contract §4.6 POST /api/v1/requests — money is integer cents
      await api.post('/api/v1/requests', {
        princess_id: parseInt(princessId, 10),
        offer_price_cents: pedicureProduct?.price_cents ?? 0,
        service_type: serviceType,
        design: nailDescription.trim(),
        special_notes: `Pedicure spoil for ${princessName}`,
        number_of_pictures: numberOfPictures,
      });
      notifications.show({
        title: `You're spoiling ${princessName}! 💅`,
        message: 'Your request has been sent. Get ready for those pics!',
        color: 'pink'
      });
      navigate('/my-requests');
    } catch (err) {
      console.error('Error creating special request:', err);
      // Pro subscription gate surfaces as 403 forbidden
      if (err?.code === 'forbidden') {
        setShowUpgradePrompt(true);
      } else {
        setSubmitError(err?.message || 'Failed to send your spoil request. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container size="sm" py="xl">
        <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
          <Loader size="lg" />
        </div>
      </Container>
    );
  }

  return (
    <Container size="sm" py="xl">
      <Stack gap="xl">
        <Button
          variant="subtle"
          leftSection={<IconArrowLeft size="1rem" />}
          onClick={() => navigate(`/princess/${princessId}`)}
          style={{ alignSelf: 'flex-start' }}
        >
          Back to {princessName}
        </Button>

        {/* Header */}
        <Stack align="center" gap="sm">
          <Avatar
            size={100}
            radius="50%"
            color="pink"
            style={{ border: '3px solid #9f38cc' }}
          >
            <IconUser size="2.5rem" />
          </Avatar>

          <Title order={1} ta="center" c="grape" size="1.8rem">
            Choose your princess, choose the color, get your pics!
          </Title>

          <Text size="md" ta="center" c="dimmed">
            You're about to spoil <strong>{princessName}</strong> with a pedicure 💅
          </Text>
        </Stack>

        {/* Price Card */}
        <Card
          shadow="sm"
          padding="lg"
          radius="lg"
          withBorder
          style={{ background: 'linear-gradient(135deg, rgba(233,45,134,0.06) 0%, rgba(159,56,204,0.06) 100%)', border: '1.5px solid rgba(159,56,204,0.2)' }}
        >
          <Group justify="space-between" align="center">
            <Stack gap={2}>
              <Group gap="xs">
                <IconSparkles size="1rem" color="var(--mantine-color-grape-5)" />
                <Text fw={600} c="grape">
                  {pedicureProduct?.title || 'Pedicure'}
                </Text>
              </Group>
              <Text size="xs" c="dimmed">Pedicure spoil amount</Text>
            </Stack>
            <Text size="2rem" fw={800} c="pink">
              {price}
            </Text>
          </Group>
        </Card>

        {/* Nail Design Form */}
        <Card shadow="sm" padding="lg" radius="lg" withBorder>
          <Stack gap="md">
            <Group gap="xs">
              <IconHeart size="1.1rem" color="var(--mantine-color-pink-5)" />
              <Text fw={700} size="md">What design do you want on {princessName}'s toes?</Text>
            </Group>
            <Text size="sm" c="dimmed">
              Describe in detail the nail polish color, design, or style you'd like to see — the more specific, the better!
            </Text>
            <Textarea
              placeholder={`e.g. "Hot pink gel nails with a glitter accent on the big toe, classic French tip on the rest. Extra shiny top coat please!"`}
              minRows={4}
              maxRows={8}
              maxLength={500}
              value={nailDescription}
              onChange={(e) => setNailDescription(e.currentTarget.value)}
              autosize
            />
            <Text size="xs" c="dimmed" ta="right">{nailDescription.length}/500</Text>
            <Group grow>
              <Select
                label="Pedicure style"
                data={[
                  { value: 'gel', label: 'Gel' },
                  { value: 'french', label: 'French' },
                  { value: 'paraffin', label: 'Paraffin' },
                  { value: 'athletic', label: 'Athletic' },
                ]}
                value={serviceType}
                onChange={(v) => setServiceType(v || 'gel')}
                allowDeselect={false}
              />
              <NumberInput
                label="Number of pictures"
                min={1}
                max={20}
                value={numberOfPictures}
                onChange={(v) => setNumberOfPictures(typeof v === 'number' ? v : 5)}
                clampBehavior="strict"
                allowDecimal={false}
              />
            </Group>
          </Stack>
        </Card>

        {/* Purchase Agreement */}
        <Paper
          p="lg"
          radius="lg"
          withBorder
          style={{ borderColor: 'rgba(159,56,204,0.3)', background: 'rgba(159,56,204,0.03)' }}
        >
          <Stack gap="md">
            <Group gap="xs">
              <IconInfoCircle size="1rem" color="var(--mantine-color-grape-5)" />
              <Text fw={600} c="grape">Spoil Agreement</Text>
            </Group>
            <Divider />
            <Text size="sm" style={{ lineHeight: 1.8 }}>
              By completing this purchase, you agree to spoil{' '}
              <strong>{princessName}</strong> with a beautiful pedicure in exchange
              for <strong>{numberOfPictures} {numberOfPictures === 1 ? 'picture' : 'pictures'} of their feet</strong> when they are done.
              Pictures will be delivered to your private messages once the
              pedicure is complete and photographed. No refunds after pictures
              have been delivered.
            </Text>
            <Checkbox
              label={
                <Text size="sm">
                  I agree to spoil <strong>{princessName}</strong> with a beautiful pedicure
                  in exchange for {numberOfPictures} {numberOfPictures === 1 ? 'picture' : 'pictures'} of their feet when they are done.
                </Text>
              }
              color="grape"
              checked={agreed}
              onChange={(e) => setAgreed(e.currentTarget.checked)}
            />
          </Stack>
        </Paper>

        {/* Pro-gate / error feedback */}
        {showUpgradePrompt && (
          <Alert
            icon={<IconExclamationCircle size="1rem" />}
            color="yellow"
            title="PayPiggyPro Required"
          >
            <Stack>
              <Text>
                Special requests are only available to PayPiggyPro subscribers.
              </Text>
              <Button
                variant="gradient"
                gradient={{ from: 'pink', to: 'cyan' }}
                onClick={() => navigate('/subscription')}
              >
                Upgrade to PayPiggyPro
              </Button>
            </Stack>
          </Alert>
        )}
        {submitError && (
          <Alert icon={<IconExclamationCircle size="1rem" />} color="red">
            {submitError}
          </Alert>
        )}

        {/* Submit */}
        <Button
          size="xl"
          fullWidth
          color="pink"
          variant="gradient"
          gradient={{ from: 'pink', to: 'grape', deg: 45 }}
          leftSection={<IconHeart size="1.3rem" />}
          disabled={!nailDescription.trim() || !agreed}
          loading={submitting}
          onClick={handleSubmit}
          style={{ fontSize: '1.1rem', padding: '1rem' }}
        >
          Let's spoil my princess 💅
        </Button>

        <Text size="xs" c="dimmed" ta="center">
          By clicking the button above you confirm your agreement to the spoil terms above.
        </Text>
      </Stack>
    </Container>
  );
};

export default SpoilPrincessPage;
